import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Screenshot as ScreenshotIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  VideoFile as VideoIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import API_CONFIG, { getApiUrl } from '../config/api';
import html2canvas from 'html2canvas';

const BugReportDialog = ({ open, onClose }) => {
  const { getCurrentUser } = useAuth();
  const [formData, setFormData] = useState({
    issueType: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [files, setFiles] = useState([]);
  const [screenshotTaken, setScreenshotTaken] = useState(false);
  const fileInputRef = useRef(null);

  const issueTypes = [
    'Bug/Error',
    'Feature Request',
    'UI/UX Issue',
    'Performance Issue',
    'Data Loss',
    'Login/Authentication',
    'Survey Creation',
    'Survey Responses',
    'Email/Notifications',
    'Other'
  ];

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = [];
    
    selectedFiles.forEach(file => {
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'video/mp4', 'video/webm', 'video/quicktime'];
      const maxSize = 10 * 1024 * 1024; // 10MB
      
      if (!allowedTypes.includes(file.type)) {
        setError(`File type ${file.type} is not supported. Please use images or videos.`);
        return;
      }
      
      if (file.size > maxSize) {
        setError(`File ${file.name} is too large. Maximum size is 10MB.`);
        return;
      }
      
      validFiles.push(file);
    });
    
    setFiles(prev => [...prev, ...validFiles]);
    // Clear the input so the same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const captureScreenshot = useCallback(async () => {
    try {
      setLoading(true);
      
      // Hide the dialog temporarily for screenshot
      const dialog = document.querySelector('[role="dialog"]');
      if (dialog) {
        dialog.style.display = 'none';
      }
      
      // Wait a bit for the dialog to hide
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Capture screenshot
      const canvas = await html2canvas(document.body, {
        height: window.innerHeight,
        width: window.innerWidth,
        scrollX: 0,
        scrollY: 0
      });
      
      // Show dialog again
      if (dialog) {
        dialog.style.display = 'block';
      }
      
      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `screenshot-${Date.now()}.png`, { type: 'image/png' });
          setFiles(prev => [...prev, file]);
          setScreenshotTaken(true);
        }
      }, 'image/png');
      
    } catch (err) {
      setError('Failed to capture screenshot: ' + err.message);
      // Make sure dialog is visible again
      const dialog = document.querySelector('[role="dialog"]');
      if (dialog) {
        dialog.style.display = 'block';
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) {
      return <ImageIcon />;
    } else if (file.type.startsWith('video/')) {
      return <VideoIcon />;
    }
    return <UploadIcon />;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getBrowserInfo = () => {
    const ua = navigator.userAgent;
    const browser = ua.match(/(chrome|firefox|safari|edge|opera)/i)?.[0] || 'Unknown';
    const version = ua.match(/version\/(\d+)/i)?.[1] || 'Unknown';
    return `${browser} ${version} - ${navigator.platform}`;
  };

  const handleSubmit = async () => {
    if (!formData.issueType || !formData.description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const currentUser = getCurrentUser();
      
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('user_id', currentUser?.uid || 'anonymous');
      formDataToSend.append('user_email', currentUser?.email || 'anonymous@example.com');
      formDataToSend.append('user_name', currentUser?.displayName || currentUser?.email || 'Anonymous User');
      formDataToSend.append('issue_type', formData.issueType);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('current_page', window.location.pathname);
      formDataToSend.append('browser_info', getBrowserInfo());
      
      // Add files to FormData
      files.forEach((file, index) => {
        formDataToSend.append(`file_${index}`, file);
      });

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SUPPORT.REPORT_BUG), {
        method: 'POST',
        body: formDataToSend // Don't set Content-Type header, let browser set it with boundary
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        throw new Error(data.message || 'Failed to submit bug report');
      }
    } catch (err) {
      setError(err.message || 'Failed to submit bug report. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ issueType: '', description: '' });
    setFiles([]);
    setScreenshotTaken(false);
    setError('');
    setSuccess(false);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        🐛 Report an Issue
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Help us improve SurveySlack by reporting bugs or suggesting features. 
          Your feedback will be sent to our support team.
        </Typography>

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thank you! Your bug report has been submitted successfully. 
            Our team will review it and get back to you if needed.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormControl fullWidth required>
            <InputLabel>Issue Type</InputLabel>
            <Select
              value={formData.issueType}
              onChange={handleChange('issueType')}
              label="Issue Type"
              disabled={loading || success}
            >
              {issueTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Description"
            multiline
            rows={6}
            value={formData.description}
            onChange={handleChange('description')}
            placeholder="Please describe the issue in detail. Include steps to reproduce if it's a bug, or explain your feature request clearly."
            required
            disabled={loading || success}
            fullWidth
          />

          {/* File Upload Section */}
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Attachments (Optional)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={loading || success}
                size="small"
              >
                Upload Files
              </Button>
              <Button
                variant="outlined"
                startIcon={<ScreenshotIcon />}
                onClick={captureScreenshot}
                disabled={loading || success}
                size="small"
              >
                Take Screenshot
              </Button>
            </Box>
            
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              multiple
              accept="image/*,video/*"
              style={{ display: 'none' }}
            />

            {/* Display uploaded files */}
            {files.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Attached files ({files.length}):
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {files.map((file, index) => (
                    <Chip
                      key={index}
                      icon={getFileIcon(file)}
                      label={`${file.name} (${formatFileSize(file.size)})`}
                      onDelete={() => removeFile(index)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      size="small"
                      disabled={loading || success}
                    />
                  ))}
                </Box>
              </Box>
            )}
            
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Supported formats: Images (JPG, PNG, GIF, WebP) and Videos (MP4, WebM, MOV). Max size: 10MB per file.
            </Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              <strong>Additional Info (automatically included):</strong><br/>
              • Current page: {window.location.pathname}<br/>
              • Browser: {getBrowserInfo()}<br/>
              • User: {getCurrentUser()?.email || 'Anonymous'}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || success || !formData.issueType || !formData.description.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Submitting...' : 'Submit Report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BugReportDialog;